<script setup>
import { usePlatformApi } from '@/utils/platformApi'

definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()
const route = useRoute('platform-tickets-id')

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const PRIORITY_META = {
  low: { label: 'Rendah', color: 'secondary' },
  medium: { label: 'Sedang', color: 'info' },
  high: { label: 'Tinggi', color: 'warning' },
  urgent: { label: 'Urgent', color: 'error' },
}

const STATUS_META = {
  open: { label: 'Terbuka', color: 'info' },
  in_progress: { label: 'Diproses', color: 'warning' },
  resolved: { label: 'Selesai', color: 'success' },
  closed: { label: 'Ditutup', color: 'secondary' },
}

const CATEGORY_LABELS = {
  billing: 'Tagihan',
  technical: 'Teknis',
  feature_request: 'Request Fitur',
  other: 'Lainnya',
}

const STATUS_OPTIONS = [
  { title: 'Terbuka', value: 'open' },
  { title: 'Diproses', value: 'in_progress' },
  { title: 'Selesai', value: 'resolved' },
  { title: 'Ditutup', value: 'closed' },
]

const PRIORITY_OPTIONS = [
  { title: 'Rendah', value: 'low' },
  { title: 'Sedang', value: 'medium' },
  { title: 'Tinggi', value: 'high' },
  { title: 'Urgent', value: 'urgent' },
]

const formatDateTime = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

// ─── Fetch ────────────────────────────────────────────────────────────────
const ticket = ref(null)
const loading = ref(true)
const fetchError = ref('')

const fetchTicket = async () => {
  loading.value = true
  fetchError.value = ''
  try {
    const res = await usePlatformApi(`/api/v1/platform/tickets/${route.params.id}`).get()
    const body = res?.data ?? res

    ticket.value = body?.data ?? body

    // Sinkronkan pilihan status/prioritas di form triase dengan data terbaru.
    triageStatus.value = ticket.value.status
    triagePriority.value = ticket.value.priority
  } catch (err) {
    if (await handleAuthError(err)) return

    fetchError.value = 'Tiket tidak ditemukan.'
    console.error('[Platform] fetch ticket error', err)
  } finally {
    loading.value = false
  }
}

// Riwayat percakapan = tiket awal (deskripsi) + semua balasan, urut
// kronologis -- sama pola dengan pages/support/[id].vue (sisi tenant).
const conversation = computed(() => {
  if (!ticket.value)
    return []

  const opening = {
    id: `ticket-${ticket.value.id}`,
    message: ticket.value.description,
    is_from_platform_admin: false,
    user: ticket.value.creator,
    created_at: ticket.value.created_at,
    isOpening: true,
  }

  return [opening, ...(ticket.value.replies ?? [])]
})

// ─── Triase: ubah status/prioritas ──────────────────────────────────────────
const triageStatus = ref(null)
const triagePriority = ref(null)
const triageLoading = ref(false)
const triageError = ref('')
const triageSuccess = ref('')

const saveTriage = async () => {
  triageLoading.value = true
  triageError.value = ''
  triageSuccess.value = ''
  try {
    const res = await usePlatformApi(`/api/v1/platform/tickets/${route.params.id}`).patch({
      body: { status: triageStatus.value, priority: triagePriority.value },
    })

    const body = res?.data ?? res

    ticket.value = body?.data ?? body
    triageSuccess.value = 'Status/prioritas berhasil diperbarui.'
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    triageError.value = data?.message ?? 'Gagal memperbarui tiket.'
  } finally {
    triageLoading.value = false
  }
}

// ─── Balas ───────────────────────────────────────────────────────────────
const replyMessage = ref('')
const replyLoading = ref(false)
const replyError = ref('')

const submitReply = async () => {
  if (!replyMessage.value.trim()) return

  replyLoading.value = true
  replyError.value = ''
  try {
    await usePlatformApi(`/api/v1/platform/tickets/${route.params.id}/replies`).post({
      body: { message: replyMessage.value },
    })

    replyMessage.value = ''
    await fetchTicket()
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    replyError.value = data?.message ?? 'Gagal mengirim balasan.'
  } finally {
    replyLoading.value = false
  }
}

onMounted(fetchTicket)
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div>
        <RouterLink to="/platform/tickets">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            class="mb-2"
          >
            Kembali ke Daftar Tiket
          </VBtn>
        </RouterLink>
        <h4 class="text-h4">
          Detail Tiket
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard
      v-if="loading"
      class="pa-10 text-center"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </VCard>

    <VCard
      v-else-if="fetchError"
      class="pa-6"
    >
      <VAlert
        type="error"
        variant="tonal"
      >
        {{ fetchError }}
      </VAlert>
    </VCard>

    <template v-else-if="ticket">
      <VCard class="mb-4">
        <VCardText class="pa-6">
          <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-3">
            <h5 class="text-h5 font-weight-bold mb-0">
              {{ ticket.subject }}
            </h5>
            <div class="d-flex gap-2">
              <VChip
                :color="PRIORITY_META[ticket.priority]?.color"
                variant="tonal"
              >
                {{ PRIORITY_META[ticket.priority]?.label ?? ticket.priority }}
              </VChip>
              <VChip
                :color="STATUS_META[ticket.status]?.color"
                variant="tonal"
              >
                {{ STATUS_META[ticket.status]?.label ?? ticket.status }}
              </VChip>
            </div>
          </div>
          <p class="text-body-2 text-medium-emphasis mb-1">
            Tenant: <strong>{{ ticket.tenant?.name ?? '-' }}</strong>
          </p>
          <p class="text-body-2 text-medium-emphasis mb-0">
            <template v-if="ticket.category">
              Kategori: {{ CATEGORY_LABELS[ticket.category] }} &middot;
            </template>
            Dibuat oleh {{ ticket.creator?.name ?? '-' }} ({{ ticket.creator?.email ?? '-' }})
            pada {{ formatDateTime(ticket.created_at) }}
          </p>
        </VCardText>
      </VCard>

      <!-- Triase: ubah status/prioritas -->
      <VCard class="mb-4">
        <VCardTitle class="pa-6 pb-2">
          Triase
        </VCardTitle>
        <VCardText class="pa-6 pt-2">
          <VAlert
            v-if="triageSuccess"
            type="success"
            variant="tonal"
            density="compact"
            class="mb-4"
            closable
            @click:close="triageSuccess = ''"
          >
            {{ triageSuccess }}
          </VAlert>
          <VAlert
            v-if="triageError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
            closable
            @click:close="triageError = ''"
          >
            {{ triageError }}
          </VAlert>

          <VRow>
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="triageStatus"
                :items="STATUS_OPTIONS"
                label="Status"
                hide-details
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="triagePriority"
                :items="PRIORITY_OPTIONS"
                label="Prioritas"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VDivider />
        <VCardActions class="pa-6">
          <VSpacer />
          <VBtn
            color="primary"
            :loading="triageLoading"
            @click="saveTriage"
          >
            Simpan Perubahan
          </VBtn>
        </VCardActions>
      </VCard>

      <!-- Riwayat Percakapan -->
      <VCard class="mb-4">
        <VCardTitle class="pa-6 pb-2">
          Riwayat Percakapan
        </VCardTitle>
        <VCardText class="pa-6 pt-2">
          <div
            v-for="msg in conversation"
            :key="msg.id"
            class="conversation-message mb-4"
            :class="msg.is_from_platform_admin ? 'conversation-message--admin' : 'conversation-message--tenant'"
          >
            <div class="d-flex align-center gap-2 mb-1">
              <VChip
                :color="msg.is_from_platform_admin ? 'primary' : 'secondary'"
                size="small"
                variant="tonal"
              >
                <VIcon
                  :icon="msg.is_from_platform_admin ? 'ri-customer-service-2-line' : 'ri-store-2-line'"
                  size="14"
                  start
                />
                {{ msg.is_from_platform_admin ? 'Admin PintuKasir (Anda)' : (msg.user?.name ?? ticket.creator?.name ?? 'Tenant') }}
              </VChip>
              <span
                v-if="msg.isOpening"
                class="text-caption text-medium-emphasis"
              >(tiket awal)</span>
              <span class="text-caption text-medium-emphasis">{{ formatDateTime(msg.created_at) }}</span>
            </div>
            <div class="conversation-bubble">
              {{ msg.message }}
            </div>
          </div>
        </VCardText>
      </VCard>

      <!-- Balas -->
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Balas sebagai Admin PintuKasir
        </VCardTitle>
        <VCardText class="pa-6 pt-2">
          <VAlert
            v-if="replyError"
            type="error"
            variant="tonal"
            class="mb-4"
            closable
            @click:close="replyError = ''"
          >
            {{ replyError }}
          </VAlert>

          <VTextarea
            v-model="replyMessage"
            label="Pesan"
            placeholder="Tulis balasan untuk tenant ini"
            rows="4"
          />
        </VCardText>
        <VDivider />
        <VCardActions class="pa-6">
          <VSpacer />
          <VBtn
            color="primary"
            :loading="replyLoading"
            :disabled="!replyMessage.trim()"
            @click="submitReply"
          >
            Kirim Balasan
          </VBtn>
        </VCardActions>
      </VCard>
    </template>
  </div>
</template>

<style scoped>
.conversation-message--admin .conversation-bubble {
  background: rgba(var(--v-theme-primary), 0.08);
  border-inline-start: 3px solid rgb(var(--v-theme-primary));
}

.conversation-message--tenant .conversation-bubble {
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-inline-start: 3px solid rgba(var(--v-theme-on-surface), 0.24);
}

.conversation-bubble {
  padding: 12px 16px;
  border-radius: 8px;
  white-space: pre-line;
}
</style>
